import { useMemo, useState } from "react";
import { AlertOctagon, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { cn } from "@/lib/utils";
import type { StationProduct } from "./api";
import { MiniBadge } from "./mini-badge";
import { byTitle, matches, mostUsed, normalize, pizzaLabel } from "./products";

const RECENT_KEY = "pizza-station:recent:v1";
const RECENT_COUNT = 6;

function readRecent(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function rememberPick(id: string) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...readRecent().filter((other) => other !== id)].slice(0, RECENT_COUNT)));
  } catch {
    // private mode: no recent picks
  }
}

interface Section {
  key: string;
  title: TranslationKey;
  products: StationProduct[];
  highlight?: boolean;
}

/**
 * The pizzas of the catalog as big tiles: the staff taps the one under the camera. On top the
 * branch's most used ones (until there are any: this tablet's last picks), then all of them by
 * name, the MINI ones apart. A tile shows the ingredients that tell the pizza apart; the search
 * finds a pizza by them too.
 */
export function PizzaPicker({
  products,
  loading,
  error,
  onRetry,
  onPick,
}: {
  products: StationProduct[] | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onPick: (product: StationProduct) => void;
}) {
  const { t } = useLocalization();
  const [query, setQuery] = useState("");
  const recentIds = useMemo(readRecent, []);

  const sections = useMemo((): Section[] => {
    const all = products ?? [];
    const q = normalize(query);
    if (q) return [{ key: "found", title: "station.products.found", products: byTitle(all.filter((p) => matches(p, q))) }];

    const top = mostUsed(all);
    const recent = recentIds.map((id) => all.find((p) => p.id === id)).filter((p): p is StationProduct => !!p);
    const regular = byTitle(all.filter((p) => !pizzaLabel(p.name).mini));
    const mini = byTitle(all.filter((p) => pizzaLabel(p.name).mini));
    return [
      top.length > 0
        ? { key: "top", title: "station.products.mostUsed" as const, products: top, highlight: true }
        : { key: "recent", title: "station.products.recent" as const, products: recent, highlight: true },
      ...(regular.length && mini.length
        ? [
            { key: "regular", title: "station.products.regular" as const, products: regular },
            { key: "mini", title: "station.products.mini" as const, products: mini },
          ]
        : [{ key: "all", title: "station.products.all" as const, products: byTitle(all) }]),
    ].filter((section) => section.products.length > 0);
  }, [products, query, recentIds]);

  const pick = (product: StationProduct) => {
    rememberPick(product.id);
    onPick(product);
  };

  if (error && !products) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <AlertOctagon className="h-12 w-12 text-red-400" aria-hidden />
        <p className="max-w-md text-lg">{t("station.products.failed", { error })}</p>
        <Button className="h-12 px-6 text-base" onClick={onRetry}>
          {t("station.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 px-4 pb-2 pt-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("station.products.search")}
            className="h-12 rounded-full pl-12 pr-12 text-base"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground active:bg-muted"
              aria-label={t("station.products.clear")}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 [overscroll-behavior:contain]">
        {loading && !products && (
          <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {Array.from({ length: 12 }, (_, i) => (
              <Skeleton key={i} className="h-24 rounded-2xl" />
            ))}
          </div>
        )}

        {products && products.length === 0 && <p className="py-10 text-center text-base text-muted-foreground">{t("station.products.none")}</p>}
        {products && products.length > 0 && sections.length === 0 && (
          <p className="py-10 text-center text-base text-muted-foreground">{t("station.products.noMatch")}</p>
        )}

        {sections.map((section) => (
          <section key={section.key} className="pt-3">
            <h2 className={cn("pb-2 text-sm font-semibold uppercase tracking-wide", section.highlight ? "text-amber-300" : "text-muted-foreground")}>
              {t(section.title)}
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {section.products.map((product) => (
                <Tile key={product.id} product={product} onPick={pick} highlight={section.highlight} showMini={section.key !== "mini"} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Tile({
  product,
  onPick,
  highlight = false,
  showMini,
}: {
  product: StationProduct;
  onPick: (p: StationProduct) => void;
  highlight?: boolean;
  showMini: boolean;
}) {
  const label = pizzaLabel(product.name);
  return (
    <button
      type="button"
      onClick={() => onPick(product)}
      className={cn(
        "flex min-h-[5.5rem] flex-col items-start justify-center gap-1 rounded-2xl border bg-card px-4 py-3 text-left shadow-sm",
        "transition-transform active:scale-[0.97] active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        highlight && "border-amber-400/40 bg-amber-400/[0.06]",
      )}
    >
      <span className="flex w-full items-center gap-2">
        {showMini && label.mini && <MiniBadge />}
        <span className="line-clamp-2 min-w-0 break-words text-lg font-semibold leading-tight">{label.title}</span>
      </span>
      {product.ingredients.length > 0 && (
        <span className="line-clamp-2 text-sm leading-snug text-muted-foreground">{product.ingredients.join(", ")}</span>
      )}
    </button>
  );
}
